import React, { useState, useEffect, useRef } from "react";
import api from "../api/homeapi";
import Swal from "sweetalert2";

/* ─── HELPERS ─── */
const getPhotoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  const BASE = `${api.defaults.baseURL}`;
  return `${BASE}${url.startsWith("/") ? "" : "/"}${url}`;
};

/* ─── ICONS ─── */
const Icon = ({ d, size = 16, fill = "none" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d={d} />
  </svg>
);
const IcoRefresh = () => <Icon d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />;

const CloseIcon    = () => <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 2l10 10M12 2L2 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>;
const CheckIcon    = () => <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8l4 4 6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const DownloadIcon = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M8 2v8M5 7l3 3 3-3M3 12h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const PhoneIcon    = () => <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M3 2h3l1.5 3.5-2 1a9 9 0 004 4l1-2 3.5 1.5V13a1 1 0 01-1 1C5 14 2 8 2 3a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/></svg>;
const CardIcon     = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.5"/><path d="M2 10h20M6 15h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>;
const FuelIcon     = () => <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><path d="M3 2h7l3 3v9H3V2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/><path d="M6 6h3M6 9h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>;
const SeatsIcon    = () => <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M5 10V5a1 1 0 011-1h12a1 1 0 011 1v5M5 10a2 2 0 00-2 2v6h18v-6a2 2 0 00-2-2M5 10h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>;
const CalIcon      = () => <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><rect x="1" y="3" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.3"/><path d="M1 7h14M5 1v4M11 1v4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>;
const ArrowIcon    = () => <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 5l4 3-4 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const ClockIcon    = () => <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.3"/><path d="M8 5v3l2 2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>;

/* ─── PAYMENT CONSTANTS ─── */
const OPERATORS = ["MVola", "Orange Money", "Airtel Money"];
const OPERATOR_NUMBERS = {
  "MVola":        "034 570 0524",
  "Orange Money": "037 313 3441",
  "Airtel Money": "033 579 2739",
};
const OPERATOR_USSD = {
  "MVola":        { code: "*MVola#", menu: "Envoyer de l'argent → Vers un numéro" },
  "Orange Money": { code: "#144#",   menu: "Transfert → Vers un numéro" },
  "Airtel Money": { code: "*185#",   menu: "Envoyer de l'argent → Vers un numéro" },
};

/* ─── MISC HELPERS ─── */
const fmtPrice = n => Number(n || 0).toLocaleString("fr-MG");
const diffDays = (from, to) => {
  if (!from || !to) return 0;
  const d = (new Date(to) - new Date(from)) / (1000 * 60 * 60 * 24);
  return Math.max(1, Math.round(d));
};

const getRentalAvailability = (rental) => {
  const available =
    rental.availability === "available" ||
    rental.status === "available" ||
    (rental.available !== false && rental.availability !== "booked");
  return available;
};

const fmtFreeDate = (rental) => {
  if (rental.nextAvailableDate) {
    return new Date(rental.nextAvailableDate).toLocaleDateString("fr-FR", {
      weekday: "long", day: "numeric", month: "long",
    });
  }
  if (rental.currentBookingEnd) {
    return new Date(rental.currentBookingEnd).toLocaleDateString("fr-FR", {
      weekday: "long", day: "numeric", month: "long",
    });
  }
  return null;
};

/* ─── LOADING / EMPTY / ERROR ─── */
const SpinnerBox = () => (
  <div style={{ textAlign: "center", padding: "60px 0", color: "#999", fontSize: 13 }}>
    <div className="tt-spin" style={{ width: 24, height: 24, border: "2px solid #e0e0e0", borderTopColor: "#3b82f6", borderRadius: "50%", margin: "0 auto 12px" }} />
    Chargement…
  </div>
);
const Empty  = ({ msg }) => <div style={{ textAlign: "center", padding: "60px 0", fontSize: 14, color: "#999" }}>{msg}</div>;
const ErrBox = ({ msg, onRetry }) => (
  <div style={{ textAlign: "center", padding: "48px 0" }}>
    <div style={{ color: "#ef4444", fontSize: 13, marginBottom: 12 }}>{msg}</div>
    {onRetry && <button style={{ fontSize: 12, padding: "7px 14px", borderRadius: 9, border: "0.5px solid #e0e0e0", background: "white", fontFamily: "inherit", color: "#666", display: "flex", alignItems: "center", gap: 6, margin: "0 auto", cursor: "pointer" }} onClick={onRetry}><IcoRefresh /> Réessayer</button>}
  </div>
);

/* ════════════════════════════════════════════════
   VEHICLE PHOTOS GALLERY
════════════════════════════════════════════════ */
const VehiclePhotos = ({ photos, availableBadge }) => {
  const [cur, setCur] = useState(0);
  const trackRef = useRef(null);
  const startXRef = useRef(0);
  const dragDxRef = useRef(0);
  const draggingRef = useRef(false);

  if (!photos || photos.length === 0) return null;

  const sorted = [...photos].sort((a, b) => {
    const aInt = /int[eé]r/i.test(a) ? 0 : 1;
    const bInt = /int[eé]r/i.test(b) ? 0 : 1;
    return aInt - bInt;
  });

  const go = (n) => {
    const next = (n + sorted.length) % sorted.length;
    setCur(next);
  };

  const isInterior = /int[eé]r/i.test(sorted[cur]);

  /* Swipe handlers */
  const onTouchStart = (e) => { startXRef.current = e.touches[0].clientX; draggingRef.current = true; };
  const onTouchMove  = (e) => { if (draggingRef.current) dragDxRef.current = e.touches[0].clientX - startXRef.current; };
  const onTouchEnd   = () => {
    if (Math.abs(dragDxRef.current) > 40) go(dragDxRef.current < 0 ? cur + 1 : cur - 1);
    draggingRef.current = false; dragDxRef.current = 0;
  };

  return (
    <div style={{ marginBottom: 0 }}>
      {/* Slider */}
      <div
        style={{ position: "relative", height: 200, overflow: "hidden", background: "#f0f0ec" }}
        onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
      >
        {/* Track */}
        <div
          ref={trackRef}
          style={{
            display: "flex", height: "100%",
            transform: `translateX(-${cur * 100}%)`,
            transition: "transform .32s cubic-bezier(.4,0,.2,1)",
            willChange: "transform",
          }}>
          {sorted.map((p, i) => (
            <div key={i} style={{ flexShrink: 0, width: "100%", height: "100%" }}>
              <img
                src={getPhotoUrl(p)} alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", pointerEvents: "none", userSelect: "none" }}
                onError={e => { e.target.style.display = "none"; }}
              />
            </div>
          ))}
        </div>

        {/* Arrows */}
        {sorted.length > 1 && (
          <>
            <button onClick={() => go(cur - 1)} style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", background: "rgba(0,0,0,0.46)", border: "none", borderRadius: 7, color: "white", width: 32, height: 32, cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2 }}>‹</button>
            <button onClick={() => go(cur + 1)} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "rgba(0,0,0,0.46)", border: "none", borderRadius: 7, color: "white", width: 32, height: 32, cursor: "pointer", fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2 }}>›</button>
          </>
        )}

        {/* Counter */}
        <div style={{ position: "absolute", bottom: 8, right: 10, fontSize: 10, color: "white", background: "rgba(0,0,0,.4)", borderRadius: 4, padding: "2px 7px", fontWeight: 600, zIndex: 2 }}>
          {cur + 1}/{sorted.length}
        </div>

        {/* Interior / Exterior label */}
        <div style={{ position: "absolute", top: 10, left: 10, fontSize: 10, color: "white", background: isInterior ? "rgba(22,163,74,0.85)" : "rgba(15,23,42,0.75)", borderRadius: 4, padding: "2px 8px", fontWeight: 600, zIndex: 2 }}>
          {isInterior ? "Intérieur" : "Extérieur"}
        </div>

        {/* Availability badge */}
        {availableBadge}
      </div>

      {/* Thumbnails */}
      {sorted.length > 1 && (
        <div style={{ display: "flex", gap: 5, padding: "6px 14px", background: "#fafaf8", overflowX: "auto" }}>
          {sorted.map((p, i) => (
            <div
              key={i} onClick={() => go(i)}
              style={{ flexShrink: 0, width: 54, height: 40, borderRadius: 6, overflow: "hidden", cursor: "pointer", border: i === cur ? "2px solid #3b82f6" : "2px solid transparent", opacity: i === cur ? 1 : 0.55, transition: "all .15s" }}>
              <img src={getPhotoUrl(p)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={e => { e.target.style.display = "none"; }} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ════════════════════════════════════════════════
   RENTAL CARD
════════════════════════════════════════════════ */
const RentalCard = ({ rental, onBook }) => {
  const available = getRentalAvailability(rental);
  const freeDate  = !available ? fmtFreeDate(rental) : null;
  const fuelColor = { Essence: "#ef4444", Diesel: "#f59e0b", Hybride: "#22c55e", "Électrique": "#3b82f6", GPL: "#8b5cf6" };

  /* Availability badge — passed into VehiclePhotos to overlay on the photo */
  const availabilityBadge = (
    <span style={{
      position: "absolute", top: 10, right: 10, fontSize: 10.5, fontWeight: 700,
      background: available ? "rgba(22,163,74,0.92)" : "rgba(239,68,68,0.92)",
      color: "white", padding: "3px 10px", borderRadius: 20,
    }}>
      {available ? "● Disponible" : "● Indisponible"}
    </span>
  );

  return (
    <div style={{ ...s.card, padding: 0, overflow: "hidden" }} className="tt-card">

      {/* ── Photo gallery (or placeholder if no photos) ── */}
      {rental.photos && rental.photos.length > 0 ? (
        <VehiclePhotos photos={rental.photos} availableBadge={availabilityBadge} />
      ) : (
        /* Fallback placeholder when no photos */
        <div style={{ height: 160, background: "#f0f0ec", position: "relative", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 6 }}>
          <span style={{ fontSize: 48 }}>🚗</span>
          <span style={{ fontSize: 11, color: "#bbb" }}>Pas de photo</span>
          {availabilityBadge}
        </div>
      )}

      {/* ── Content ── */}
      <div style={{ padding: "16px 20px 20px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", letterSpacing: -0.3 }}>
              {rental.brand} {rental.model}
              {rental.year && <span style={{ fontSize: 11, color: "#999", fontWeight: 400, marginLeft: 6 }}>{rental.year}</span>}
            </div>
            {rental.licensePlate && (
              <div style={{ fontSize: 11, color: "#94a3b8", fontFamily: "monospace", letterSpacing: 1, marginTop: 2 }}>{rental.licensePlate}</div>
            )}
          </div>
          <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 12 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#1a1a1a", letterSpacing: -0.5 }}>{fmtPrice(rental.dailyPrice)}</div>
            <div style={{ fontSize: 10, color: "#999" }}>MGA / jour</div>
          </div>
        </div>

        {/* Tags */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
          {rental.fuelType && (
            <span style={{ fontSize: 10.5, display: "flex", alignItems: "center", gap: 4, color: fuelColor[rental.fuelType] ?? "#666", background: `${(fuelColor[rental.fuelType] ?? "#666")}14`, padding: "3px 9px", borderRadius: 20, fontWeight: 600 }}>
              <FuelIcon /> {rental.fuelType}
            </span>
          )}
          {rental.seats && (
            <span style={{ fontSize: 10.5, display: "flex", alignItems: "center", gap: 4, color: "#64748b", background: "#f4f4f0", padding: "3px 9px", borderRadius: 20 }}>
              <SeatsIcon /> {rental.seats} places
            </span>
          )}
          {rental.features?.slice(0, 3).map(f => (
            <span key={f} style={{ fontSize: 10.5, color: "#64748b", background: "#f4f4f0", padding: "3px 9px", borderRadius: 20 }}>{f}</span>
          ))}
          {rental.features?.length > 3 && (
            <span style={{ fontSize: 10.5, color: "#bbb" }}>+{rental.features.length - 3}</span>
          )}
        </div>

        {rental.description && (
          <div style={{ fontSize: 12, color: "#94a3b8", lineHeight: 1.5, marginBottom: 12, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
            {rental.description}
          </div>
        )}

        {/* "Free from" notice when booked */}
        {!available && (
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            background: "#fef3c7", border: "0.5px solid #fcd34d",
            borderRadius: 10, padding: "10px 14px", marginBottom: 14,
          }}>
            <span style={{ color: "#d97706", flexShrink: 0 }}><ClockIcon /></span>
            <div>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: "#92400e" }}>Véhicule actuellement loué</div>
              {freeDate
                ? <div style={{ fontSize: 11.5, color: "#78350f", marginTop: 2 }}>
                    Disponible à partir du <strong>{freeDate}</strong>
                  </div>
                : <div style={{ fontSize: 11.5, color: "#78350f", marginTop: 2 }}>
                    Contactez-nous pour connaître la prochaine disponibilité
                  </div>
              }
            </div>
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
          {rental.bookings != null && (
            <span style={{ fontSize: 11, color: "#bbb" }}>⭐ {rental.bookings} réservation{rental.bookings !== 1 ? "s" : ""}</span>
          )}
          <button
            disabled={!available}
            onClick={() => available && onBook(rental)}
            style={{
              fontSize: 12, padding: "9px 18px", borderRadius: 9, fontFamily: "inherit", fontWeight: 600,
              flexShrink: 0, cursor: available ? "pointer" : "not-allowed", border: "none",
              background: available ? "#1a1a1a" : "#e0e0e0", color: available ? "white" : "#bbb",
              display: "flex", alignItems: "center", gap: 7, marginLeft: "auto",
            }}>
            <ArrowIcon /> {available ? "Réserver ce véhicule" : "Indisponible"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════════════
   RENTAL INFO MODAL (step 1)
════════════════════════════════════════════════ */
const RentalInfoModal = ({ car, onClose, onContinue }) => {
  const today = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState({ startDate: "", endDate: "", customerName: "", customerPhone: "", customerEmail: "", pickupLocation: "" });
  const [err, setErr]   = useState("");

  const days  = diffDays(form.startDate, form.endDate);
  const total = days * Number(car.dailyPrice || 0);
  const set   = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.startDate)        { setErr("Sélectionnez une date de début."); return false; }
    if (!form.endDate)          { setErr("Sélectionnez une date de fin."); return false; }
    if (form.endDate <= form.startDate) { setErr("La date de fin doit être après la date de début."); return false; }
    if (!form.customerName.trim())  { setErr("Votre nom est requis."); return false; }
    if (!form.customerPhone.trim()) { setErr("Votre numéro de téléphone est requis."); return false; }
    return true;
  };

  return (
    <Overlay onClose={onClose}>
      <div style={modalStyle}>
        <ModalHeader title={`Réserver — ${car.brand} ${car.model}`} onClose={onClose} />
        <div style={{ padding: "0 24px 24px" }}>
          {/* Car summary */}
          <div style={{ display: "flex", gap: 14, alignItems: "center", background: "#f8fafc", borderRadius: 12, padding: "14px 16px", marginBottom: 18, border: "0.5px solid #e2e8f0" }}>
            <div style={{ width: 64, height: 52, borderRadius: 9, overflow: "hidden", background: "#e8e8e4", flexShrink: 0 }}>
              {car.photos?.[0]
                ? <img src={getPhotoUrl(car.photos[0])} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={e => e.target.style.display = "none"} />
                : <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>🚗</div>}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#1a1a1a" }}>{car.brand} {car.model} {car.year ? `(${car.year})` : ""}</div>
              <div style={{ fontSize: 12, color: "#999", marginTop: 2 }}>{fmtPrice(car.dailyPrice)} MGA / jour</div>
            </div>
          </div>

          {/* Dates */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
            <div style={s.formField}>
              <label style={s.fieldLabel}><CalIcon /> Date de début *</label>
              <input type="date" style={s.fieldInput} min={today} value={form.startDate}
                onChange={e => set("startDate", e.target.value)} />
            </div>
            <div style={s.formField}>
              <label style={s.fieldLabel}><CalIcon /> Date de fin *</label>
              <input type="date" style={s.fieldInput} min={form.startDate || today} value={form.endDate}
                onChange={e => set("endDate", e.target.value)} />
            </div>
          </div>

          {/* Total */}
          {form.startDate && form.endDate && form.endDate > form.startDate && (
            <div style={{ background: "#eff6ff", border: "0.5px solid #bfdbfe", borderRadius: 10, padding: "12px 16px", marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 12, color: "#3b82f6" }}>
                <strong>{days}</strong> jour{days > 1 ? "s" : ""} × {fmtPrice(car.dailyPrice)} MGA
              </div>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#1e3a5f" }}>{fmtPrice(total)} MGA</div>
            </div>
          )}

          <div style={{ background: "#fffbeb", border: "0.5px solid #fde68a", borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#92400e" }}>
            ℹ️ Aucun compte requis — renseignez simplement vos coordonnées.
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={s.formField}>
              <label style={s.fieldLabel}>Nom complet *</label>
              <input style={s.fieldInput} placeholder="Ex : Rakoto Jean" value={form.customerName} onChange={e => set("customerName", e.target.value)} />
            </div>
            <div style={s.formField}>
              <label style={s.fieldLabel}>Téléphone *</label>
              <input style={s.fieldInput} placeholder="Ex : 034 12 345 67" type="tel" value={form.customerPhone} onChange={e => set("customerPhone", e.target.value)} />
            </div>
            <div style={s.formField}>
              <label style={s.fieldLabel}>Email (optionnel)</label>
              <input style={s.fieldInput} placeholder="Ex : rakoto@email.com" type="email" value={form.customerEmail} onChange={e => set("customerEmail", e.target.value)} />
            </div>
            <div style={s.formField}>
              <label style={s.fieldLabel}>Lieu de prise en charge (optionnel)</label>
              <input style={s.fieldInput} placeholder="Ex : Aéroport d'Ivato, Antananarivo" value={form.pickupLocation} onChange={e => set("pickupLocation", e.target.value)} />
            </div>
          </div>

          {err && <div style={{ background: "#fef2f2", border: "0.5px solid #fecaca", borderRadius: 9, padding: "10px 14px", fontSize: 13, color: "#ef4444", marginTop: 10 }}>{err}</div>}

          <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
            <button style={btnGhost} onClick={onClose}>Annuler</button>
            <button style={{ ...btnPrimary, flex: 1, justifyContent: "center", background: "#3b82f6" }}
              onClick={() => { setErr(""); if (!validate()) return; onContinue({ ...form, days, total }); }}>
              Continuer → Paiement
            </button>
          </div>
        </div>
      </div>
    </Overlay>
  );
};

/* ════════════════════════════════════════════════
   PAYMENT MODAL
════════════════════════════════════════════════ */
const PaymentModal = ({ amount, reference, onClose, onSuccess }) => {
  const [payMethod, setPayMethod] = useState("mobile");
  const [operator,  setOperator]  = useState("");
  const [txRef,     setTxRef]     = useState("");
  const [step,      setStep]      = useState(1);
  const [loading,   /*setLoading*/]   = useState(false);
  const [err,       setErr]       = useState("");

  const handleMobileNext = () => {
    if (step === 1) {
      if (!operator) { setErr("Choisissez un opérateur"); return; }
      setStep(2); setErr("");
    } else {
      if (!txRef.trim()) { setErr("Saisissez la référence de transaction"); return; }
      onSuccess({ operator, txRef });
    }
  };

  const StepBar = ({ current }) => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18 }}>
      <div style={{ ...stepDot, ...(current >= 1 ? stepDotActive : {}) }}>1</div>
      <div style={stepLine} />
      <div style={{ ...stepDot, ...(current >= 2 ? stepDotActive : {}) }}>2</div>
    </div>
  );

  return (
    <Overlay onClose={onClose}>
      <div style={modalStyle}>
        <ModalHeader title="Paiement" onClose={onClose} />
        <div style={{ padding: "0 24px 24px" }}>
          <div style={{ background: "#eff6ff", borderRadius: 12, padding: "16px 20px", marginBottom: 16, textAlign: "center", border: "0.5px solid #bfdbfe" }}>
            <div style={{ fontSize: 11, color: "#3b82f6", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 4 }}>Montant à payer</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: "#1e3a5f", letterSpacing: -0.5 }}>{fmtPrice(amount)} <span style={{ fontSize: 14, color: "#999" }}>MGA</span></div>
            {reference && <div style={{ fontSize: 11, color: "#999", marginTop: 4 }}>Réf : {reference}</div>}
          </div>

          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            {[["mobile", <PhoneIcon />, "Mobile Money"], ["card", <CardIcon />, "Carte bancaire"]].map(([key, icon, label]) => (
              <button key={key} style={{ ...payMethodBtn, ...(payMethod === key ? payMethodBtnActive : {}) }}
                onClick={() => { setPayMethod(key); setErr(""); setStep(1); }}>
                {icon} {label}
              </button>
            ))}
          </div>

          {payMethod === "mobile" && (
            <>
              <StepBar current={step} />
              {step === 1 && (
                <>
                  <label style={fieldLabelStyle}>Choisissez votre opérateur *</label>
                  <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
                    {OPERATORS.map(op => (
                      <button key={op} style={{ ...opBtn, ...(operator === op ? opBtnActive : {}) }}
                        onClick={() => { setOperator(op); setErr(""); }}>
                        {op}
                      </button>
                    ))}
                  </div>
                  {operator && (
                    <div style={{ background: "#fffbeb", borderRadius: 10, padding: "12px 14px", border: "0.5px solid #fde68a", marginBottom: 12 }}>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: "#92400e", marginBottom: 6 }}>📲 Comment payer avec {operator}</div>
                      <p style={{ fontSize: 12, color: "#78350f", lineHeight: 1.6, margin: 0 }}>
                        1. Composez <strong>{OPERATOR_USSD[operator].code}</strong><br/>
                        2. Sélectionnez <strong>{OPERATOR_USSD[operator].menu}</strong><br/>
                        3. Entrez le numéro : <strong style={{ fontSize: 14, color: "#92400e", letterSpacing: 1 }}>{OPERATOR_NUMBERS[operator]}</strong><br/>
                        4. Montant : <strong>{fmtPrice(amount)} MGA</strong><br/>
                        5. Confirmez avec votre code PIN<br/>
                        6. Notez la <strong>référence de transaction</strong> reçue par SMS
                      </p>
                    </div>
                  )}
                </>
              )}
              {step === 2 && (
                <>
                  <div style={{ background: "#eff6ff", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13, color: "#1a1a1a", border: "0.5px solid #bfdbfe" }}>
                    <div style={{ marginBottom: 4 }}>Opérateur : <strong>{operator}</strong></div>
                    <div>Numéro destinataire : <strong style={{ color: "#3b82f6" }}>{OPERATOR_NUMBERS[operator]}</strong></div>
                    <div style={{ marginTop: 4, fontSize: 12, color: "#999" }}>Montant : {fmtPrice(amount)} MGA</div>
                  </div>
                  <label style={fieldLabelStyle}>Référence de transaction reçue par SMS *</label>
                  <input style={inputField2} placeholder="Ex : MVL-2025-XXXXXX"
                    value={txRef} onChange={e => { setTxRef(e.target.value); setErr(""); }} />
                  <p style={{ fontSize: 11.5, color: "#999", marginTop: 6, lineHeight: 1.5 }}>Copiez exactement la référence reçue par SMS après votre paiement.</p>
                </>
              )}
            </>
          )}

          {payMethod === "card" && (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "32px 16px", textAlign: "center", gap: 14 }}>
              <div style={{ fontSize: 40 }}>💳</div>
              <div style={{ background: "#fffbeb", border: "0.5px solid #fde68a", borderRadius: 12, padding: "16px 20px", width: "100%" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#92400e", marginBottom: 6 }}>🚧 Service bientôt disponible</div>
                <div style={{ fontSize: 12.5, color: "#78350f", lineHeight: 1.65 }}>Utilisez <strong>Mobile Money</strong> en attendant.</div>
              </div>
            </div>
          )}

          {err && <p style={{ color: "#ef4444", fontSize: 12, marginTop: 8 }}>{err}</p>}

          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <button style={btnGhost} onClick={() => {
              if (step === 1 || payMethod === "card") onClose();
              else { setStep(1); setErr(""); }
            }}>← Retour</button>
            {payMethod === "mobile" && (
              <button style={{ ...btnPrimary, flex: 1, background: "#3b82f6" }} onClick={handleMobileNext} disabled={loading}>
                {step === 1 ? "Suivant →" : "Confirmer le paiement ✓"}
              </button>
            )}
          </div>
        </div>
      </div>
    </Overlay>
  );
};

/* ════════════════════════════════════════════════
   RENTAL TICKET MODAL
════════════════════════════════════════════════ */
const RentalTicketModal = ({ booking, car, operator, onClose }) => {
  const downloadTicket = () => {
    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Réservation Location - ${booking.reference || "REF"}</title>
<style>
  body{font-family:'Helvetica Neue',sans-serif;background:#fafaf8;margin:0;padding:40px}
  .ticket{max-width:480px;margin:auto;background:white;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,.1)}
  .header{background:linear-gradient(135deg,#1e3a5f,#3b82f6);color:white;padding:28px 32px 20px}
  .logo{font-size:22px;font-weight:800;margin-bottom:4px}.logo span{color:#93c5fd}
  .ref{font-size:11px;opacity:.7;letter-spacing:1px;text-transform:uppercase}
  .title{font-size:22px;font-weight:700;margin:10px 0 4px}
  .body{padding:24px 32px}
  .row{display:flex;justify-content:space-between;padding:10px 0;border-bottom:.5px solid #f0f0f0}
  .row:last-child{border-bottom:none}
  .label{font-size:11px;color:#999;text-transform:uppercase;letter-spacing:.5px}
  .val{font-size:13px;font-weight:600;color:#1a1a1a}
  .footer{background:#eff6ff;padding:16px 32px;text-align:center;font-size:11px;color:#3b82f6;font-weight:600}
</style></head><body>
<div class="ticket">
  <div class="header">
    <div class="logo">Trans<span>TiGo</span> Location</div>
    <div class="ref">Réf : ${booking.reference || "—"}</div>
    <div class="title">🚗 ${car.brand} ${car.model}</div>
  </div>
  <div class="body">
    <div class="row"><div class="label">Client</div><div class="val">${booking.customerName || "—"}</div></div>
    <div class="row"><div class="label">Téléphone</div><div class="val">${booking.customerPhone || "—"}</div></div>
    <div class="row"><div class="label">Du</div><div class="val">${booking.startDate || "—"}</div></div>
    <div class="row"><div class="label">Au</div><div class="val">${booking.endDate || "—"}</div></div>
    <div class="row"><div class="label">Durée</div><div class="val">${booking.days || "—"} jour(s)</div></div>
    <div class="row"><div class="label">Total</div><div class="val">${Number(booking.totalPrice || 0).toLocaleString()} MGA</div></div>
    ${booking.pickupLocation ? `<div class="row"><div class="label">Lieu de prise en charge</div><div class="val">${booking.pickupLocation}</div></div>` : ""}
    <div class="row"><div class="label">Opérateur</div><div class="val">${operator || "—"}</div></div>
    <div class="row"><div class="label">Statut</div><div class="val" style="color:#16a34a">✓ Réservé</div></div>
  </div>
  <div class="footer">TransTiGo Madagascar · Location de véhicules</div>
</div></body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = `location-${booking.reference || "ticket"}.html`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Overlay onClose={onClose}>
      <div style={{ ...modalStyle, maxWidth: 480 }}>
        <ModalHeader title="Réservation confirmée ! 🎉" onClose={onClose} />
        <div style={{ padding: "0 24px 24px" }}>
          <div style={{ borderRadius: 14, overflow: "hidden", border: "0.5px solid #e0e0e0", marginBottom: 16 }}>
            <div style={{ background: "linear-gradient(135deg, #1e3a5f, #3b82f6)", color: "white", padding: "20px 22px 16px" }}>
              <div style={{ fontSize: 16, fontWeight: 800, letterSpacing: -0.5, marginBottom: 6 }}>Trans<span style={{ color: "#93c5fd" }}>TiGo</span> <span style={{ fontSize: 12, fontWeight: 500, opacity: 0.8 }}>Location</span></div>
              <div style={{ fontSize: 19, fontWeight: 700 }}>🚗 {car.brand} {car.model}</div>
              {booking.reference && <div style={{ fontSize: 10.5, opacity: 0.7, letterSpacing: "0.8px", textTransform: "uppercase", marginTop: 6 }}>Réf : {booking.reference}</div>}
            </div>
            <div style={{ padding: "16px 20px" }}>
              {[
                ["Client",       booking.customerName],
                ["Téléphone",    booking.customerPhone],
                ["Du",           booking.startDate],
                ["Au",           booking.endDate],
                ["Durée",        booking.days ? `${booking.days} jour${booking.days > 1 ? "s" : ""}` : null],
                ["Lieu",         booking.pickupLocation || null],
              ].filter(([, v]) => v).map(([label, val]) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: "0.5px solid #f8f8f6" }}>
                  <span style={{ fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.4px", fontWeight: 600 }}>{label}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a" }}>{val}</span>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", marginTop: 4 }}>
                <span style={{ fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.4px", fontWeight: 600 }}>Total payé</span>
                <span style={{ fontSize: 18, fontWeight: 800, color: "#1e3a5f" }}>{fmtPrice(booking.totalPrice)} MGA</span>
              </div>
            </div>
            <div style={{ background: "#eff6ff", padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: "#3b82f6" }}><CheckIcon /> Réservation confirmée</div>
              <div style={{ fontSize: 10, letterSpacing: "3px", color: "#ccc", fontFamily: "monospace" }}>||| {booking.reference || "—"} |||</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button style={btnGhost} onClick={() => window.location.reload()}>Fermer</button>
            <button style={{ ...btnPrimary, flex: 1, justifyContent: "center", background: "#3b82f6" }}
              onClick={() => { downloadTicket(); window.location.reload(); }}>
              <DownloadIcon /> Télécharger le bon
            </button>
          </div>
        </div>
      </div>
    </Overlay>
  );
};

/* ════════════════════════════════════════════════
   HEADER
════════════════════════════════════════════════ */
const Header = () => (
  <header style={s.header}>
    <a href="/" style={s.logo}>Trans<span style={{ color: "#16a34a" }}>TiGo</span></a>
    <nav style={s.nav}>
      <a href="/covoiturage" style={s.navBtn}>Covoiturage</a>
      <a href="/location" style={{ ...s.navBtn, ...s.navBtnActive }}>Location</a>
    </nav>
  </header>
);

/* ════════════════════════════════════════════════
   MAIN PAGE — LOCATION
════════════════════════════════════════════════ */
export default function LocationPage() {
  const [rentals, setRentals]   = useState([]);
  const [status,  setStatus]    = useState("loading");

  const [flowCar,     setFlowCar]     = useState(null);
  const [flowStep,    setFlowStep]    = useState(null);
  const [bookingInfo, setBookingInfo] = useState(null);
  const [bookingData, setBookingData] = useState(null);
  const [ticketOp,    setTicketOp]    = useState(null);

  const load = async () => {
    setStatus("loading");
    try {
      const res = await api.get("/rentals");
      setRentals(res.data);
      setStatus("ok");
    } catch { setStatus("error"); }
  };
  useEffect(() => { load(); }, []);

  const handleBook = (car) => {
    setFlowCar(car);
    setBookingInfo(null);
    setBookingData(null);
    setFlowStep("info");
  };

  const handleInfoContinue = (info) => {
    setBookingInfo(info);
    setFlowStep("pay");
  };

  const handlePaySuccess = async ({ operator, txRef }) => {
    try {
      const res = await api.post("/rental-bookings", {
        carId:          flowCar.id,
        startDate:      bookingInfo.startDate,
        endDate:        bookingInfo.endDate,
        customerName:   bookingInfo.customerName,
        customerPhone:  bookingInfo.customerPhone,
        customerEmail:  bookingInfo.customerEmail  || null,
        pickupLocation: bookingInfo.pickupLocation || null,
        operator,
        transactionRef: txRef,
      });
      setBookingData({
        ...res.data,
        customerName:   bookingInfo.customerName,
        customerPhone:  bookingInfo.customerPhone,
        startDate:      bookingInfo.startDate,
        endDate:        bookingInfo.endDate,
        days:           bookingInfo.days,
        totalPrice:     bookingInfo.total,
        pickupLocation: bookingInfo.pickupLocation,
      });
      setTicketOp(operator);
      setFlowStep("ticket");
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.error || "Erreur lors de la réservation.";
      Swal.fire({ icon: "error", title: "Erreur", text: msg, confirmButtonColor: "#d33" });
    }
  };

  const closeFlow = () => { setFlowStep(null); setFlowCar(null); };

  const availableCount = rentals.filter(r => getRentalAvailability(r)).length;

  return (
    <div style={s.page}>
      <style>{css}</style>
      <Header />

      {/* Hero */}
      <div style={s.hero}>
        <div style={s.heroBadge}><span style={s.badgeDot} /> Véhicules disponibles</div>
        <h1 style={s.heroTitle}>
          Louez <em style={{ color: "#3b82f6", fontStyle: "normal" }}>un</em> véhicule
        </h1>
        <p style={s.heroSub}>Berlines, SUV, 4x4 — trouvez le véhicule adapté à votre aventure.</p>
      </div>

      {/* Filter bar */}
      <div style={{ maxWidth: 900, margin: "20px auto 0", padding: "0 20px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div style={{ fontSize: 13, color: "#999" }}>
          {status === "ok" && `${availableCount} véhicule${availableCount !== 1 ? "s" : ""} disponible${availableCount !== 1 ? "s" : ""} sur ${rentals.length}`}
        </div>
        <button style={{ fontSize: 12, padding: "7px 14px", borderRadius: 9, border: "0.5px solid #e0e0e0", background: "white", fontFamily: "inherit", color: "#666", display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }} onClick={load}>
          <IcoRefresh /> Actualiser
        </button>
      </div>

      {/* Cards */}
      <div style={s.cardList}>
        {status === "loading" && <SpinnerBox />}
        {status === "error"   && <ErrBox msg="Impossible de charger les véhicules." onRetry={load} />}
        {status === "ok" && rentals.length === 0 && <Empty msg="Aucun véhicule disponible pour le moment." />}
        {status === "ok" && rentals.map(r => (
          <RentalCard key={r.id} rental={r} onBook={handleBook} />
        ))}
      </div>

      {/* Eco band */}
      <div style={s.ecoBand}>
        {[
          { num: "100+", label: "véhicules référencés" },
          { num: "24/7", label: "assistance client" },
          { num: "2 min", label: "pour réserver" },
        ].map(({ num, label }, i, arr) => (
          <React.Fragment key={label}>
            <div style={s.ecoItem}>
              <span style={s.ecoNum}>{num}</span>
              <span style={s.ecoLabel}>{label}</span>
            </div>
            {i < arr.length - 1 && <div style={s.ecoDivider} />}
          </React.Fragment>
        ))}
      </div>

      <footer style={s.footer}>
        <div style={{ fontSize: 13, color: "#999" }}>© 2025 TransTigo · Madagascar</div>
        <div style={{ display: "flex", gap: 16 }}>
          {["Confidentialité", "Conditions", "Contact"].map(l => (
            <a key={l} href={`/${l.toLowerCase()}`} style={{ fontSize: 12, color: "#bbb", textDecoration: "none" }}>{l}</a>
          ))}
        </div>
      </footer>

      {/* Rental flow modals */}
      {flowStep === "info" && flowCar && (
        <RentalInfoModal car={flowCar} onClose={closeFlow} onContinue={handleInfoContinue} />
      )}
      {flowStep === "pay" && bookingInfo && flowCar && (
        <PaymentModal
          amount={bookingInfo.total}
          reference={null}
          onClose={closeFlow}
          onSuccess={handlePaySuccess}
        />
      )}
      {flowStep === "ticket" && bookingData && flowCar && (
        <RentalTicketModal
          booking={bookingData}
          car={flowCar}
          operator={ticketOp}
          onClose={closeFlow}
        />
      )}
    </div>
  );
}

/* ─── SHARED ATOMS ─── */
const overlayStyle = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", backdropFilter: "blur(8px)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };
const modalStyle   = { background: "white", borderRadius: 20, width: "100%", maxWidth: 460, maxHeight: "92vh", overflowY: "auto", boxShadow: "0 32px 80px rgba(0,0,0,0.25)" };
const modalHeaderStyle = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 24px 16px", borderBottom: "0.5px solid #f0f0f0", position: "sticky", top: 0, background: "white", zIndex: 1, borderRadius: "20px 20px 0 0" };
const Overlay     = ({ children, onClose }) => <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>{children}</div>;
const ModalHeader = ({ title, onClose }) => (
  <div style={modalHeaderStyle}>
    <span style={{ fontSize: 15, fontWeight: 700, color: "#1a1a1a" }}>{title}</span>
    <button style={{ background: "none", border: "none", cursor: "pointer", color: "#999", display: "flex", padding: 4 }} onClick={onClose}><CloseIcon /></button>
  </div>
);

const payMethodBtn       = { flex: 1, padding: "9px 10px", borderRadius: 9, border: "0.5px solid #e0e0e0", cursor: "pointer", background: "none", fontFamily: "inherit", fontSize: 12, color: "#666", fontWeight: 500, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 };
const payMethodBtnActive = { background: "#1a1a1a", color: "white", borderColor: "#1a1a1a" };
const opBtn              = { flex: 1, padding: "8px 6px", borderRadius: 8, border: "0.5px solid #e0e0e0", cursor: "pointer", background: "none", fontFamily: "inherit", fontSize: 11.5, color: "#666", fontWeight: 500 };
const opBtnActive        = { background: "#1e3a5f", color: "white", borderColor: "#1e3a5f" };
const stepDot            = { width: 28, height: 28, borderRadius: "50%", border: "2px solid #e0e0e0", background: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#ccc", flexShrink: 0 };
const stepDotActive      = { border: "2px solid #3b82f6", background: "#3b82f6", color: "white" };
const stepLine           = { width: 60, height: 2, background: "#e0e0e0", margin: "0 4px" };
const fieldLabelStyle    = { fontSize: 11, fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: "0.5px", display: "block", marginBottom: 6 };
const inputField2        = { width: "100%", padding: "10px 12px", border: "0.5px solid #e0e0e0", borderRadius: 9, fontSize: 13.5, fontFamily: "inherit", color: "#1a1a1a", background: "#fafaf8", outline: "none", boxSizing: "border-box" };
const btnPrimary         = { padding: "10px 18px", background: "#1a1a1a", color: "white", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6 };
const btnGhost           = { padding: "9px 16px", background: "none", border: "0.5px solid #d0d0d0", borderRadius: 10, cursor: "pointer", fontFamily: "inherit", color: "#444", fontSize: 13, display: "flex", alignItems: "center", gap: 5 };

const s = {
  page:         { fontFamily: "'Plus Jakarta Sans', sans-serif", background: "#fafaf8", color: "#1a1a1a", minHeight: "100vh" },
  header:       { position: "sticky", top: 0, zIndex: 100, background: "rgba(250,250,248,0.95)", backdropFilter: "blur(12px)", borderBottom: "0.5px solid rgba(0,0,0,0.07)", padding: "0 24px", display: "flex", alignItems: "center", gap: 16, height: 60 },
  logo:         { fontSize: 18, fontWeight: 700, letterSpacing: -0.5, color: "#1a1a1a", textDecoration: "none" },
  nav:          { display: "flex", gap: 4, flex: 1, justifyContent: "center" },
  navBtn:       { display: "flex", alignItems: "center", gap: 6, fontSize: 13, padding: "7px 14px", borderRadius: 9, border: "none", background: "none", fontFamily: "inherit", color: "#666", fontWeight: 500, textDecoration: "none" },
  navBtnActive: { background: "#1a1a1a", color: "white" },

  hero:      { padding: "48px 24px 28px", textAlign: "center" },
  heroBadge: { display: "inline-flex", alignItems: "center", gap: 6, background: "#eff6ff", border: "0.5px solid #bfdbfe", padding: "5px 14px", borderRadius: 20, fontSize: 12, color: "#3b82f6", fontWeight: 500, marginBottom: 16 },
  badgeDot:  { width: 6, height: 6, background: "#3b82f6", borderRadius: "50%", display: "inline-block" },
  heroTitle: { fontSize: "clamp(28px, 6vw, 44px)", fontWeight: 700, letterSpacing: -1.5, marginBottom: 10, lineHeight: 1.1 },
  heroSub:   { fontSize: 15, color: "#666", lineHeight: 1.7, maxWidth: 520, margin: "0 auto" },

  cardList:  { maxWidth: 900, margin: "0 auto", padding: "8px 20px 64px", display: "flex", flexDirection: "column", gap: 16 },
  card:      { background: "white", border: "0.5px solid #e8e8e4", borderRadius: 14, display: "flex", flexDirection: "column" },

  formField:  { display: "flex", flexDirection: "column", gap: 5 },
  fieldLabel: { fontSize: 10, color: "#999", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, display: "flex", alignItems: "center", gap: 4 },
  fieldInput: { padding: "10px 12px", border: "0.5px solid #e0e0e0", borderRadius: 9, fontSize: 13.5, fontFamily: "inherit", color: "#1a1a1a", background: "#fafaf8", outline: "none", width: "100%", boxSizing: "border-box" },

  ecoBand:   { background: "#f4f4f0", padding: "28px 24px", display: "flex", alignItems: "center", justifyContent: "center", gap: 0, flexWrap: "wrap" },
  ecoItem:   { flex: "1 1 120px", display: "flex", flexDirection: "column", alignItems: "center", gap: 4, padding: "10px 0" },
  ecoNum:    { fontSize: 26, fontWeight: 700, letterSpacing: -1, color: "#1a1a1a" },
  ecoLabel:  { fontSize: 11, color: "#999", textAlign: "center" },
  ecoDivider:{ width: "0.5px", height: 36, background: "#e0e0e0", flexShrink: 0 },
  footer:    { padding: "20px 24px", borderTop: "0.5px solid #e8e8e4", display: "flex", alignItems: "center", justifyContent: "space-between", background: "white", flexWrap: "wrap", gap: 8 },
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
  * { box-sizing: border-box; }
  body { margin: 0; }
  .tt-card { transition: box-shadow .2s, transform .15s; }
  .tt-card:hover { box-shadow: 0 6px 28px rgba(0,0,0,0.08); transform: translateY(-1px); }
  input:focus, select:focus { border-color: #3b82f6 !important; box-shadow: 0 0 0 3px rgba(59,130,246,0.1) !important; }
  @keyframes tt-spin { to { transform: rotate(360deg); } }
  .tt-spin { animation: tt-spin .8s linear infinite; }
  @media (max-width: 640px) {
    nav { display: none !important; }
  }
`;